import React, { useEffect, useState, useCallback, useRef } from "react";
import useApi from "@/hooks/useApi";
import { TRIGGER_TYPES, COMING_SOON_TRIGGERS, GLOBAL_VARIABLES } from "./triggerTypes";
import TemplateEditor from "./TemplateEditor";
import ComparisonChart from "./ComparisonChart";
import {
  AreaChart, Area,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Quota {
  enableOutboundCampaigns: boolean;
  enableSms: boolean;
  maxContacts: number;
  maxEmailsPerMonth: number;
  contactCount: number;
  currentMonthEmailCount: number;
  remainingEmails: number | null;
  termsAccepted: boolean;
  termsAcceptedAt?: string;
  hasVerifiedDomain: boolean;
  verifiedDomains: string[];
}

interface ContactList {
  id: number;
  name: string;
  description: string;
  color: string;
  memberCount: number;
}

interface Member {
  id: number;
  email: string;
  name: string;
  phone: string;
  tags: string;
  addedAt: string;
}

interface Segment {
  id: number;
  name: string;
  description: string;
  estimatedCount: number;
  conditionsJson: string;
}

interface Broadcast {
  id: number;
  name: string;
  channel: "Email" | "Sms";
  status: "Draft" | "Scheduled" | "Sending" | "Sent" | "Cancelled";
  listId: number | null;
  segmentId: number | null;
  subject: string;
  fromName: string;
  fromAddress: string;
  scheduledAt: string | null;
  sentAt: string | null;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  openedCount: number;
  clickedCount: number;
  createdAt: string;
}

interface Template {
  id: number;
  name: string;
  category: string;
  subject: string;
  bodyHtml: string;
  bodyText: string;
  fromName: string;
  fromAddress: string;
}

interface Campaign {
  id: number;
  name: string;
  triggerType: string;
  isActive: boolean;
  channel: string;
  webhookToken?: string;
  steps: any[];
  stats: { enrolled: number; sent: number };
  requiredSubscriptionFlag?: string;
}

// ─── Feature Landing Page ─────────────────────────────────────────────────────

function FeatureLandingPage() {
  return (
    <div className="max-w-3xl mx-auto py-16 px-6">
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-2xl mb-4">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Outbound Campaigns</h1>
        <p className="text-gray-500 text-base max-w-xl mx-auto">
          Send targeted email and SMS campaigns to your contacts — powered by the same conversation data your team already uses.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        {[
          {
            icon: "👥",
            title: "Contact Lists",
            desc: "Import contacts via CSV, segment by behavior, tags, or any field. Build audiences Mailchimp can't match — because we know what your customers actually said in chat."
          },
          {
            icon: "📢",
            title: "Broadcast Campaigns",
            desc: "Send a targeted email or SMS to an entire list in one click. Schedule for the perfect send time. Track opens, clicks, and unsubscribes in real time."
          },
          {
            icon: "🔄",
            title: "Automated Sequences",
            desc: "Trigger multi-step drip sequences from real events: conversation ended, negative sentiment detected, new contact created, ticket resolved. Already built-in."
          },
          {
            icon: "📊",
            title: "Smart Segmentation",
            desc: "Filter contacts by email domain, tags, source, last conversation date, sentiment score, or custom fields. Target the right 100 people, not the wrong 10,000."
          },
          {
            icon: "📱",
            title: "SMS Campaigns",
            desc: "Same platform, same contact lists — just pick SMS as your channel. 98% open rate vs 22% for email. Planned for TextConnect customers."
          },
          {
            icon: "🛡️",
            title: "CAN-SPAM & GDPR Compliant",
            desc: "Every email includes a signed 1-click unsubscribe link. Suppression lists are enforced automatically. You stay compliant without thinking about it."
          }
        ].map(f => (
          <div key={f.title} className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="text-2xl mb-2">{f.icon}</div>
            <h3 className="font-semibold text-gray-900 mb-1">{f.title}</h3>
            <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 text-center">
        <h3 className="font-semibold text-gray-900 mb-1">Ready to activate?</h3>
        <p className="text-sm text-gray-500 mb-4">
          Outbound Campaigns is a premium add-on. Contact your account manager or Velaro support to enable it for your account.
          {/* TODO(billing): Replace this entire block with <UpgradeButton feature="Outbound Email Campaigns" plan="Add-on ($99/mo)" /> → Stripe Checkout. See docs/self-serve-billing-todo.md */}
        </p>
        <a
          href="mailto:support@velaro.com?subject=Enable Outbound Campaigns"
          className="inline-block bg-blue-600 text-white text-sm font-medium px-5 py-2 rounded-lg hover:bg-blue-700"
        >
          Contact Support to Enable →
        </a>
      </div>
    </div>
  );
}

// ─── Contacts Tab ─────────────────────────────────────────────────────────────

function ContactsTab({ quota }: { quota: Quota }) {
  const api = useApi();
  const [lists, setLists] = useState<ContactList[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeList, setActiveList] = useState<ContactList | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [memberPage, setMemberPage] = useState(1);
  const [memberTotal, setMemberTotal] = useState(0);
  const [memberSearch, setMemberSearch] = useState("");
  const [editList, setEditList] = useState<ContactList | null | "new">(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.admin.get("ContactLists");
      const d = await r.json();
      setLists(Array.isArray(d) ? d : []);
    } catch { setLists([]); }
    finally { setLoading(false); }
  }, []);

  const loadSegments = useCallback(async () => {
    try {
      const r = await api.admin.get("ContactSegments");
      const d = await r.json();
      setSegments(Array.isArray(d) ? d : []);
    } catch { setSegments([]); }
  }, []);

  const loadMembers = useCallback(async (list: ContactList, page = 1, search = "") => {
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: "50" });
      if (search) params.set("search", search);
      const r = await api.admin.get(`ContactLists/${list.id}/Members?${params}`);
      const d = await r.json();
      setMembers(Array.isArray(d?.items) ? d.items : []);
      setMemberTotal(d?.total ?? 0);
    } catch { setMembers([]); }
  }, []);

  useEffect(() => { loadLists(); loadSegments(); }, [loadLists, loadSegments]);
  useEffect(() => {
    if (activeList) loadMembers(activeList, memberPage, memberSearch);
  }, [activeList, memberPage, memberSearch, loadMembers]);

  const saveList = async (name: string, description: string, color: string) => {
    if (editList === "new") {
      await api.admin.post("ContactLists", { name, description, color });
    } else if (editList) {
      await api.admin.put(`ContactLists/${editList.id}`, { name, description, color });
    }
    setEditList(null);
    await loadLists();
  };

  const deleteList = async (id: number) => {
    if (!confirm("Delete this list and all its contacts?")) return;
    await api.admin.delete(`ContactLists/${id}`);
    if (activeList?.id === id) setActiveList(null);
    await loadLists();
  };

  const handleImport = async () => {
    if (!importFile || !activeList) return;
    setImporting(true);
    setImportStatus("Uploading…");
    try {
      const form = new FormData();
      form.append("file", importFile);
      const r = await fetch(`/api/admin/ContactLists/${activeList.id}/Import`, {
        method: "POST",
        body: form,
        credentials: "include"
      });
      const d = await r.json();
      if (!r.ok) { setImportStatus(`Error: ${d.error}`); return; }

      // Poll for completion
      const importId = d.importId;
      setImportStatus("Processing…");
      for (let i = 0; i < 60; i++) {
        await new Promise(res => setTimeout(res, 2000));
        const sr = await api.admin.get(`ContactLists/Imports/${importId}`);
        const sd = await sr.json();
        if (sd.status === "Complete") {
          setImportStatus(`Done! ${sd.importedCount} imported, ${sd.skippedCount} updated, ${sd.errorCount} errors.`);
          setImportFile(null);
          if (fileRef.current) fileRef.current.value = "";
          await loadLists();
          await loadMembers(activeList, 1, "");
          return;
        }
        if (sd.status === "Failed") {
          setImportStatus(`Failed: ${sd.errorSample ?? "Unknown error"}`);
          return;
        }
        setImportStatus(`Processing… (${sd.importedCount}/${sd.totalRows})`);
      }
      setImportStatus("Timed out. Check back in a moment.");
    } catch (e) {
      setImportStatus("Upload failed.");
    } finally {
      setImporting(false);
    }
  };

  const exportList = (list: ContactList) => {
    window.open(`/api/admin/ContactLists/${list.id}/Export`, "_blank");
  };

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>;

  return (
    <div className="flex gap-6">
      {/* List sidebar */}
      <div className="w-64 shrink-0 space-y-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Lists</span>
          <button onClick={() => setEditList("new")} className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">+ New</button>
        </div>
        {lists.map(list => (
          <button
            key={list.id}
            onClick={() => { setActiveList(list); setMemberPage(1); setMemberSearch(""); }}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${activeList?.id === list.id ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-700 hover:bg-gray-50"}`}
          >
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: list.color }} />
              <span className="truncate flex-1">{list.name}</span>
              <span className="text-xs text-gray-400 shrink-0">{list.memberCount.toLocaleString()}</span>
            </div>
          </button>
        ))}
        {lists.length === 0 && (
          <p className="text-xs text-gray-400 px-1">No lists yet. Create one to get started.</p>
        )}

        {/* Quota bar */}
        <div className="mt-6 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs font-medium text-gray-500 mb-1">Contacts</p>
          <p className="text-lg font-bold text-gray-900">{quota.contactCount.toLocaleString()}</p>
          {quota.maxContacts > 0 && (
            <>
              <div className="mt-1 h-1.5 bg-gray-200 rounded-full">
                <div className="h-1.5 bg-blue-500 rounded-full" style={{ width: `${Math.min(100, quota.contactCount / quota.maxContacts * 100)}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">of {quota.maxContacts.toLocaleString()} limit</p>
            </>
          )}
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 min-w-0">
        {!activeList ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-sm">Select a list to view contacts, or create a new list.</p>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">{activeList.name}</h2>
                <p className="text-xs text-gray-500">{memberTotal.toLocaleString()} contacts</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditList(activeList)} className="text-xs px-3 py-1.5 border border-gray-200 rounded hover:bg-gray-50">Edit</button>
                <button onClick={() => exportList(activeList)} className="text-xs px-3 py-1.5 border border-gray-200 rounded hover:bg-gray-50">Export CSV</button>
                <button onClick={() => deleteList(activeList.id)} className="text-xs px-3 py-1.5 border border-red-200 text-red-600 rounded hover:bg-red-50">Delete</button>
              </div>
            </div>

            {/* Import */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="text-xs font-medium text-gray-700 mb-2">Import contacts via CSV</p>
              <p className="text-xs text-gray-500 mb-2">Required column: <code>email</code>. Optional: <code>name</code>, <code>phone</code>, <code>tags</code></p>
              <div className="flex gap-2 items-center">
                <input ref={fileRef} type="file" accept=".csv" onChange={e => setImportFile(e.target.files?.[0] ?? null)}
                  className="text-xs text-gray-600 file:mr-2 file:text-xs file:px-2 file:py-1 file:border file:border-gray-200 file:rounded file:bg-white" />
                <button
                  onClick={handleImport}
                  disabled={!importFile || importing}
                  className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 whitespace-nowrap"
                >
                  {importing ? "Importing…" : "Import"}
                </button>
              </div>
              {importStatus && <p className="text-xs mt-1 text-gray-600">{importStatus}</p>}
            </div>

            {/* Search */}
            <div className="mb-3">
              <input
                type="search" placeholder="Search by email or name…"
                className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm"
                value={memberSearch}
                onChange={e => { setMemberSearch(e.target.value); setMemberPage(1); }}
              />
            </div>

            {/* Members table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Email</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Name</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Tags</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Added</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {members.map(m => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-900">{m.email}</td>
                      <td className="px-3 py-2 text-gray-600">{m.name || "—"}</td>
                      <td className="px-3 py-2">
                        {m.tags ? m.tags.split(",").filter(Boolean).map(t => (
                          <span key={t} className="inline-block bg-gray-100 text-gray-600 rounded px-1.5 py-0.5 mr-1 text-xs">{t.trim()}</span>
                        )) : "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-400">{new Date(m.addedAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {members.length === 0 && (
                    <tr><td colSpan={4} className="px-3 py-8 text-center text-gray-400">No contacts yet. Import a CSV to get started.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {memberTotal > 50 && (
              <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                <span>{(memberPage - 1) * 50 + 1}–{Math.min(memberPage * 50, memberTotal)} of {memberTotal.toLocaleString()}</span>
                <div className="flex gap-1">
                  <button disabled={memberPage === 1} onClick={() => setMemberPage(p => p - 1)}
                    className="px-2 py-1 border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50">Prev</button>
                  <button disabled={memberPage * 50 >= memberTotal} onClick={() => setMemberPage(p => p + 1)}
                    className="px-2 py-1 border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50">Next</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit list modal */}
      {editList && <ListEditModal list={editList === "new" ? null : editList} onSave={saveList} onClose={() => setEditList(null)} />}
    </div>
  );
}

function ListEditModal({ list, onSave, onClose }: { list: ContactList | null; onSave: (name: string, desc: string, color: string) => void; onClose: () => void }) {
  const [name, setName] = useState(list?.name ?? "");
  const [desc, setDesc] = useState(list?.description ?? "");
  const [color, setColor] = useState(list?.color ?? "#3B82F6");
  const COLORS = ["#3B82F6","#10B981","#F59E0B","#EF4444","#8B5CF6","#EC4899","#6B7280"];
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-sm shadow-2xl p-6">
        <h2 className="text-base font-semibold mb-4">{list ? "Edit List" : "New List"}</h2>
        <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
        <input className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm mb-3"
          value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Newsletter Subscribers" />
        <label className="block text-xs font-medium text-gray-500 mb-1">Description (optional)</label>
        <input className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm mb-3"
          value={desc} onChange={e => setDesc(e.target.value)} />
        <label className="block text-xs font-medium text-gray-500 mb-2">Color</label>
        <div className="flex gap-2 mb-4">
          {COLORS.map(c => (
            <button key={c} onClick={() => setColor(c)}
              className={`w-6 h-6 rounded-full border-2 ${color === c ? "border-gray-900 scale-110" : "border-transparent"}`}
              style={{ background: c }} />
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-1.5 text-sm border border-gray-200 rounded">Cancel</button>
          <button onClick={() => onSave(name, desc, color)} disabled={!name.trim()} className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-40">Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── Broadcasts Tab ───────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  Draft: "bg-gray-100 text-gray-600",
  Scheduled: "bg-blue-100 text-blue-700",
  Sending: "bg-yellow-100 text-yellow-700",
  Sent: "bg-green-100 text-green-700",
  Cancelled: "bg-red-100 text-red-600",
};

function BroadcastsTab({ lists, quota }: { lists: ContactList[]; quota: Quota }) {
  const api = useApi();
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Broadcast | null | "new">(null);
  const [analyticsId, setAnalyticsId] = useState<number | null>(null);
  const [resendingId, setResendingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [br, tr] = await Promise.all([
        api.admin.get("BroadcastCampaigns").then(r => r.json()),
        api.admin.get("EmailCampaigns/Templates").then(r => r.json()),
      ]);
      setBroadcasts(Array.isArray(br) ? br : []);
      setTemplates(Array.isArray(tr) ? tr : []);
    } catch { setBroadcasts([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteBroadcast = async (id: number) => {
    if (!confirm("Delete this broadcast campaign?")) return;
    await api.admin.delete(`BroadcastCampaigns/${id}`);
    await load();
  };

  const cancelBroadcast = async (id: number) => {
    if (!confirm("Cancel this scheduled campaign?")) return;
    await api.admin.post(`BroadcastCampaigns/${id}/cancel`, {});
    await load();
  };

  const resendNonOpeners = async (b: Broadcast) => {
    setResendingId(b.id);
    try {
      const r = await api.admin.post(`BroadcastCampaigns/${b.id}/ResendNonOpeners`, {
        name: `${b.name} (Re-send to non-openers)`,
        subject: `Did you see this? → ${b.subject}`,
      });
      const d = await r.json();
      if (!r.ok) { alert(d.error ?? "Could not create re-send."); return; }
      alert(`${d.message} Opening draft editor…`);
      await load();
    } finally { setResendingId(null); }
  };

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-gray-500">
            One-time sends to a contact list or segment.
            {quota.remainingEmails != null && (
              <span className="ml-2 text-blue-600 font-medium">{quota.remainingEmails.toLocaleString()} emails remaining this month</span>
            )}
          </p>
        </div>
        <button onClick={() => setEditing("new")} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
          + New Broadcast
        </button>
      </div>

      {broadcasts.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-sm mb-1">No broadcasts yet.</p>
          <p className="text-xs">Create a broadcast to send a one-time email or SMS to a contact list.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {broadcasts.map(b => (
            <div key={b.id} className="px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-medium text-gray-900 truncate">{b.name}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[b.status] ?? "bg-gray-100 text-gray-600"}`}>
                      {b.status}
                    </span>
                    <span className="text-xs text-gray-400">{b.channel === "Email" ? "📧" : "📱"}</span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    {b.subject && `"${b.subject}" · `}
                    {b.status === "Sent" ? `${(b.sentCount ?? 0).toLocaleString()} sent` : b.scheduledAt ? `Scheduled ${new Date(b.scheduledAt).toLocaleString()}` : "Draft"}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4 shrink-0">
                  {b.status === "Sent" && (
                    <button
                      onClick={() => setAnalyticsId(b.id)}
                      className="text-xs px-3 py-1 border border-blue-200 text-blue-600 rounded hover:bg-blue-50 font-medium"
                    >
                      📊 Analytics
                    </button>
                  )}
                  {b.status === "Sent" && b.channel === "Email" && (b.openedCount ?? 0) < (b.sentCount ?? 1) && (
                    <button
                      onClick={() => resendNonOpeners(b)}
                      disabled={resendingId === b.id}
                      className="text-xs px-3 py-1 border border-purple-200 text-purple-700 rounded hover:bg-purple-50 font-medium disabled:opacity-40"
                    >
                      {resendingId === b.id ? "Creating…" : "↩ Non-openers"}
                    </button>
                  )}
                  {(b.status === "Draft" || b.status === "Scheduled") && (
                    <button onClick={() => setEditing(b)} className="text-xs px-3 py-1 border border-gray-200 rounded hover:bg-gray-50">Edit</button>
                  )}
                  {b.status === "Scheduled" && (
                    <button onClick={() => cancelBroadcast(b.id)} className="text-xs px-3 py-1 border border-orange-200 text-orange-600 rounded hover:bg-orange-50">Cancel</button>
                  )}
                  {(b.status === "Draft" || b.status === "Cancelled") && (
                    <button onClick={() => deleteBroadcast(b.id)} className="text-xs px-3 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50">Delete</button>
                  )}
                </div>
              </div>
              {/* Analytics mini-bar for sent emails */}
              {b.status === "Sent" && b.channel === "Email" && (b.sentCount ?? 0) > 0 && (
                <div className="flex gap-4 mt-2 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Open rate</span>
                    <span className="text-xs font-semibold text-emerald-600">
                      {b.sentCount > 0 ? `${Math.round(((b.openedCount ?? 0) / b.sentCount) * 100)}%` : "—"}
                    </span>
                    <span className="text-xs text-gray-400">({b.openedCount ?? 0} opens)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Click rate</span>
                    <span className="text-xs font-semibold text-blue-600">
                      {b.sentCount > 0 ? `${Math.round(((b.clickedCount ?? 0) / b.sentCount) * 100)}%` : "—"}
                    </span>
                    <span className="text-xs text-gray-400">({b.clickedCount ?? 0} clicks)</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <BroadcastEditorModal
          broadcast={editing === "new" ? null : editing}
          lists={lists}
          templates={templates}
          quota={quota}
          onSave={async () => { setEditing(null); await load(); }}
          onClose={() => setEditing(null)}
        />
      )}
      {analyticsId !== null && (
        <BroadcastAnalyticsModal id={analyticsId} onClose={() => setAnalyticsId(null)} />
      )}
    </div>
  );
}

function BroadcastAnalyticsModal({ id, onClose }: { id: number; onClose: () => void }) {
  const api = useApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.admin.get(`BroadcastCampaigns/${id}/Analytics`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id, api.admin]);

  const funnelSteps = data ? [
    { label: "Sent", count: data.sentCount, color: "bg-slate-400" },
    { label: "Opened", count: data.openedCount, color: "bg-emerald-500" },
    { label: "Clicked", count: data.clickedCount, color: "bg-blue-500" },
  ] : [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-[600px] max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Campaign Analytics</h3>
            {data?.campaign && <p className="text-xs text-slate-500 mt-0.5">{data.campaign.name}</p>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl" aria-label="Close">×</button>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          {loading ? (
            <div className="text-sm text-slate-400 text-center py-16">Loading…</div>
          ) : !data ? (
            <div className="text-sm text-slate-400 text-center py-16">No data available.</div>
          ) : (
            <div className="space-y-6">
              {/* KPI cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <p className="text-2xl font-bold text-slate-900">{(data.sentCount ?? 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-500 mt-1 font-medium">Delivered</p>
                </div>
                <div className="text-center p-4 bg-emerald-50 rounded-xl">
                  <p className="text-2xl font-bold text-emerald-700">{data.openRate ?? 0}%</p>
                  <p className="text-xs text-emerald-600 mt-1 font-medium">Open Rate</p>
                  <p className="text-xs text-slate-400">{data.openedCount ?? 0} unique opens</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <p className="text-2xl font-bold text-blue-700">{data.clickRate ?? 0}%</p>
                  <p className="text-xs text-blue-600 mt-1 font-medium">Click Rate</p>
                  <p className="text-xs text-slate-400">{data.clickedCount ?? 0} unique clicks</p>
                </div>
              </div>

              {/* Funnel */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">Engagement Funnel</h4>
                <div className="space-y-2">
                  {funnelSteps.map((step, i) => {
                    const pct = funnelSteps[0].count > 0 ? (step.count / funnelSteps[0].count * 100) : 0;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs w-14 text-slate-500">{step.label}</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-3">
                          <div className={`${step.color} h-3 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-slate-600 w-20 text-right font-medium">
                          {step.count.toLocaleString()} ({pct.toFixed(1)}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
                {data.clickToOpenRate > 0 && (
                  <p className="text-xs text-slate-500 mt-3">
                    Click-to-open rate: <strong className="text-slate-700">{data.clickToOpenRate}%</strong>
                    <span className="ml-1 text-slate-400">(of openers who clicked)</span>
                  </p>
                )}
              </div>

              {/* Hourly open curve — recharts BarChart */}
              {Array.isArray(data.hourlyOpens) && data.hourlyOpens.length > 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">Opens Over Time (first 72h)</h4>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart
                      data={data.hourlyOpens.map((h: any) => ({ hour: `${h.hourOffset}h`, opens: h.opens ?? 0 }))}
                      margin={{ top: 2, right: 8, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis
                        dataKey="hour"
                        tick={{ fontSize: 10, fill: "#94a3b8" }}
                        axisLine={false}
                        tickLine={false}
                        interval={Math.floor(data.hourlyOpens.length / 6)}
                      />
                      <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={28} />
                      <Tooltip
                        contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }}
                        formatter={(value: any) => [value, "Opens"]}
                        labelFormatter={(label: any) => `Hour ${label}`}
                      />
                      <Bar dataKey="opens" fill="#34d399" radius={[3, 3, 0, 0]} maxBarSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="text-xs text-slate-400 mt-1">Hour 0 = send time</p>
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                  <p className="text-xs text-slate-500 font-medium">Hourly open data available after 24h</p>
                  <p className="text-xs text-slate-400 mt-0.5">Check back once the campaign has been sent for at least 24 hours.</p>
                </div>
              )}

              {/* A/B test result */}
              {data.abTestWinnerVariant && (
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">A/B Test Result</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {(["A", "B"] as const).map(variant => {
                      const isWinner = data.abTestWinnerVariant === variant;
                      const opens = variant === "A" ? (data.abVariantAOpens ?? 0) : (data.abVariantBOpens ?? 0);
                      const subject = variant === "A" ? (data.abVariantASubject ?? `Subject A`) : (data.abVariantBSubject ?? `Subject B`);
                      return (
                        <div
                          key={variant}
                          className={`relative p-4 rounded-xl border-2 ${isWinner ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white"}`}
                        >
                          {isWinner && (
                            <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide">
                              WINNER
                            </span>
                          )}
                          <p className="text-xs font-semibold text-slate-500 mb-1">Subject {variant}</p>
                          <p className="text-xs text-slate-700 mb-3 leading-snug line-clamp-2">{subject}</p>
                          <p className={`text-2xl font-bold ${isWinner ? "text-emerald-600" : "text-slate-700"}`}>
                            {opens.toLocaleString()}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">unique opens</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Top clickers */}
              {Array.isArray(data.topClickers) && data.topClickers.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">Top Clickers</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {data.topClickers.map((c: any, i: number) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div>
                          <span className="text-slate-700 font-medium">{c.email}</span>
                          {c.name && <span className="text-slate-400 ml-2 text-xs">{c.name}</span>}
                        </div>
                        <span className="text-xs text-blue-600 font-medium">{c.clickCount} clicks</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BroadcastEditorModal({ broadcast, lists, templates, quota, onSave, onClose }: {
  broadcast: Broadcast | null;
  lists: ContactList[];
  templates: Template[];
  quota: Quota;
  onSave: () => void;
  onClose: () => void;
}) {
  const api = useApi();
  const [form, setForm] = useState({
    name: broadcast?.name ?? "",
    channel: broadcast?.channel === "Sms" ? "sms" : "email",
    listId: (broadcast?.listId ?? (lists[0]?.id ?? null)) as number | null,
    subject: broadcast?.subject ?? "",
    bodyHtml: "",
    bodyText: "",
    fromName: broadcast?.fromName ?? "",
    fromAddress: broadcast?.fromAddress ?? "",
    sendNow: true,
    scheduledAt: "",
    abTestEnabled: false,
    abTestSubjectB: "",
    abTestSamplePct: 20,
  });
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState<"setup" | "content" | "review">("setup");
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  // savedId: set from existing broadcast OR after first auto-save for new ones
  const [savedId, setSavedId] = useState<number | null>(broadcast?.id ?? null);

  const applyTemplate = (id: number) => {
    const t = templates.find(x => x.id === id);
    if (!t) return;
    setForm(f => ({ ...f, subject: t.subject, bodyHtml: t.bodyHtml, bodyText: t.bodyText, fromName: t.fromName, fromAddress: t.fromAddress }));
    setTemplateId(id);
  };

  const buildPayload = () => ({
    name: form.name || "Untitled Campaign",
    channel: form.channel,
    listId: form.listId,
    segmentId: null,
    subject: form.subject,
    bodyHtml: form.bodyHtml,
    bodyText: form.bodyText,
    smsBody: form.bodyText,
    fromName: form.fromName,
    fromAddress: form.fromAddress,
    scheduledAt: form.sendNow ? null : form.scheduledAt || null,
    abTestSubjectB: form.abTestEnabled ? form.abTestSubjectB || null : null,
    abTestSamplePct: form.abTestSamplePct,
  });

  // Returns the campaign ID, auto-saving as draft if not yet saved.
  const ensureSaved = async (): Promise<number | null> => {
    if (savedId) {
      // Already saved — just update in place so content is current
      await api.admin.put(`BroadcastCampaigns/${savedId}`, buildPayload());
      return savedId;
    }
    const r = await api.admin.post("BroadcastCampaigns", buildPayload());
    if (!r.ok) return null;
    const d = await r.json();
    setSavedId(d.id);
    return d.id;
  };

  const handleSave = async (schedule: boolean) => {
    setSaving(true);
    try {
      const id = await ensureSaved();
      if (!id) return;
      if (schedule) {
        await api.admin.post(`BroadcastCampaigns/${id}/schedule`, { sendAt: form.sendNow ? null : form.scheduledAt });
      }
      onSave();
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) return;
    setTestSending(true);
    setTestResult(null);
    try {
      // Auto-save first if this is a new campaign so we have an ID
      const id = await ensureSaved();
      if (!id) { setTestResult({ ok: false, msg: "Could not save campaign before sending test." }); return; }
      const r = await api.admin.post(`BroadcastCampaigns/${id}/SendTest`, { email: testEmail.trim() });
      const d = await r.json();
      setTestResult({ ok: r.ok, msg: d.message ?? d.error ?? (r.ok ? "Sent!" : "Failed") });
    } catch {
      setTestResult({ ok: false, msg: "Network error. Check that the email channel is configured." });
    }
    setTestSending(false);
  };

  const selectedList = lists.find(l => l.id === form.listId);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{broadcast ? "Edit Broadcast" : "New Broadcast"}</h2>
            <div className="flex gap-2 mt-1">
              {(["setup", "content", "review"] as const).map((s, i) => (
                <button key={s} onClick={() => setStep(s)}
                  className={`text-xs px-2 py-0.5 rounded ${step === s ? "bg-blue-100 text-blue-700" : "text-gray-400 hover:text-gray-600"}`}>
                  {i + 1}. {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {step === "setup" && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Campaign Name</label>
                <input className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm"
                  value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. March Newsletter" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Channel</label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" value="email" checked={form.channel === "email"} onChange={() => setForm(f => ({ ...f, channel: "email" }))} />
                    <span className="text-sm">📧 Email</span>
                  </label>
                  <label className={`flex items-center gap-2 ${!quota.enableSms ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}>
                    <input type="radio" value="sms" checked={form.channel === "sms"} disabled={!quota.enableSms}
                      onChange={() => quota.enableSms && setForm(f => ({ ...f, channel: "sms" }))} />
                    <span className="text-sm">📱 SMS {!quota.enableSms && <span className="text-xs text-gray-400">(upgrade required)</span>}</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Audience — Contact List</label>
                <select className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm"
                  value={form.listId ?? ""} onChange={e => setForm(f => ({ ...f, listId: Number(e.target.value) || null }))}>
                  <option value="">— select a list —</option>
                  {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.memberCount.toLocaleString()} contacts)</option>)}
                </select>
                {selectedList && quota.maxEmailsPerMonth > 0 && selectedList.memberCount > quota.remainingEmails! && (
                  <p className="text-xs text-red-500 mt-1">
                    This list has {selectedList.memberCount.toLocaleString()} contacts but you only have {quota.remainingEmails?.toLocaleString()} sends remaining this month.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Send Time</label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={form.sendNow} onChange={() => setForm(f => ({ ...f, sendNow: true }))} />
                    <span className="text-sm">Send immediately</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={!form.sendNow} onChange={() => setForm(f => ({ ...f, sendNow: false }))} />
                    <span className="text-sm">Schedule for later</span>
                  </label>
                </div>
                {!form.sendNow && (
                  <input type="datetime-local" className="mt-2 w-full border border-gray-200 rounded px-3 py-1.5 text-sm"
                    value={form.scheduledAt} onChange={e => setForm(f => ({ ...f, scheduledAt: e.target.value }))} />
                )}
              </div>

              {form.channel === "email" && (
                <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-800">A/B Subject Line Test</p>
                      <p className="text-xs text-gray-500 mt-0.5">Test two subject lines on a sample — automatically send the winner to the rest.</p>
                    </div>
                    <button
                      onClick={() => setForm(f => ({ ...f, abTestEnabled: !f.abTestEnabled }))}
                      className={`w-10 h-5 rounded-full relative transition-colors ${form.abTestEnabled ? "bg-purple-500" : "bg-gray-300"}`}
                      aria-label="Toggle A/B test"
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.abTestEnabled ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </div>
                  {form.abTestEnabled && (
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Subject A (your main subject)</label>
                        <p className="text-xs text-gray-400 italic">{form.subject || "— set in Content step —"}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Subject B (variant)</label>
                        <input className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm bg-white"
                          value={form.abTestSubjectB}
                          onChange={e => setForm(f => ({ ...f, abTestSubjectB: e.target.value }))}
                          placeholder="e.g. Did you see this? 👀" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Sample size per variant</label>
                        <div className="flex items-center gap-2">
                          <input type="range" min="5" max="45" step="5" className="flex-1"
                            value={form.abTestSamplePct}
                            onChange={e => setForm(f => ({ ...f, abTestSamplePct: +e.target.value }))} />
                          <span className="text-sm font-medium text-gray-700 w-12 text-right">{form.abTestSamplePct}%</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Each subject gets {form.abTestSamplePct}% of the list. The remaining {100 - form.abTestSamplePct * 2}% receive the winner after 4 hours.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {step === "content" && (
            <>
              {templates.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Start from a template (optional)</label>
                  <select className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm"
                    value={templateId ?? ""} onChange={e => e.target.value && applyTemplate(Number(e.target.value))}>
                    <option value="">— write from scratch —</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              )}
              {form.channel === "email" ? (
                <TemplateEditor
                  template={{ subject: form.subject, bodyHtml: form.bodyHtml, bodyText: form.bodyText, fromName: form.fromName, fromAddress: form.fromAddress, category: "custom", name: "" }}
                  onChange={(field, val) => setForm(f => ({ ...f, [field]: val }))}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">SMS Message</label>
                  <textarea className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm h-24 resize-none"
                    value={form.bodyText} onChange={e => setForm(f => ({ ...f, bodyText: e.target.value }))}
                    placeholder="Your SMS message… (160 chars per segment)" maxLength={1600} />
                  <p className="text-xs text-gray-400 mt-1">{form.bodyText.length}/160 chars · Use {`<<contact.firstName>>`} to personalize</p>
                </div>
              )}
            </>
          )}

          {step === "review" && (
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Review & Send</h3>
              <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
                <div className="flex justify-between"><span className="text-gray-500">Campaign</span><span className="font-medium">{form.name || "—"}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Channel</span><span>{form.channel === "email" ? "📧 Email" : "📱 SMS"}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Audience</span><span>{selectedList ? `${selectedList.name} (${selectedList.memberCount.toLocaleString()})` : "—"}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Subject</span><span className="truncate max-w-[200px]">{form.subject || "—"}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Send</span><span>{form.sendNow ? "Immediately" : form.scheduledAt ? new Date(form.scheduledAt).toLocaleString() : "—"}</span></div>
              </div>
              {quota.remainingEmails != null && selectedList && selectedList.memberCount > quota.remainingEmails && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  Not enough remaining quota. You have {quota.remainingEmails.toLocaleString()} emails left this month but this list has {selectedList.memberCount.toLocaleString()} contacts.
                </div>
              )}

              {/* Send Test — works on new campaigns too (auto-saves as draft first) */}
              {form.channel === "email" && (
                <div className="border border-dashed border-gray-200 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-medium text-gray-600">Send a test email to yourself before launching</p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      className="flex-1 border border-gray-200 rounded px-2 py-1 text-sm"
                      placeholder="you@example.com"
                      value={testEmail}
                      onChange={e => { setTestEmail(e.target.value); setTestResult(null); }}
                    />
                    <button
                      onClick={sendTest}
                      disabled={testSending || !testEmail.trim()}
                      className="px-3 py-1 text-sm border border-blue-200 text-blue-600 rounded hover:bg-blue-50 disabled:opacity-40 whitespace-nowrap">
                      {testSending ? "Sending…" : "Send Test"}
                    </button>
                  </div>
                  {testResult && (
                    <p className={`text-xs ${testResult.ok ? "text-green-700" : "text-red-600"}`}>{testResult.msg}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Domain gate warning on review step */}
        {step === "review" && form.channel === "email" && !quota.hasVerifiedDomain && (
          <div className="px-6 py-2 bg-amber-50 border-t border-amber-200 flex items-start gap-2">
            <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-xs text-amber-700">
              You need a verified sending domain before you can send. Go to <strong>Settings → Sending Domains</strong> to add and verify your domain.
            </p>
          </div>
        )}

        <div className="px-6 py-3 border-t border-gray-200 flex justify-between gap-2">
          <button onClick={onClose} className="px-4 py-1.5 text-sm border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
          <div className="flex gap-2">
            {step !== "setup" && <button onClick={() => setStep(s => s === "review" ? "content" : "setup")} className="px-4 py-1.5 text-sm border border-gray-200 rounded">Back</button>}
            {step !== "review" && <button onClick={() => setStep(s => s === "setup" ? "content" : "review")} disabled={!form.name.trim() || !form.listId} className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-40">Next →</button>}
            {step === "review" && (
              <>
                <button onClick={() => handleSave(false)} disabled={saving} className="px-4 py-1.5 text-sm border border-gray-200 rounded disabled:opacity-40">Save Draft</button>
                <button
                  onClick={() => handleSave(true)}
                  disabled={saving || !form.name.trim() || !form.listId || (form.channel === "email" && !quota.hasVerifiedDomain)}
                  title={form.channel === "email" && !quota.hasVerifiedDomain ? "Add and verify a sending domain first (Settings → Sending Domains)" : undefined}
                  className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-40 font-medium">
                  {saving ? "Saving…" : form.sendNow ? "Send Now" : "Schedule →"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sequences Tab (existing drip campaigns) ──────────────────────────────────

const BLANK_CAMPAIGN = {
  name: "", triggerType: "conversation_ended", triggerConditionsJson: "",
  requiredSubscriptionFlag: "", isActive: true, enrollmentCooldownHours: 24,
  maxEnrollmentsPerContact: 1, steps: [] as { stepOrder: number; delayDays: number; delayHours: number; templateId: number }[],
};

// Quick-start flow templates for the sequences empty state
const SEQUENCE_QUICK_STARTS = [
  {
    icon: "🎯",
    name: "Trial / Signup Welcome",
    trigger: "trial_started",
    description: "Fire when someone starts a trial. Onboard them with Day 0 welcome, Day 2 tip, Day 5 check-in.",
    cooldownHours: 0,
  },
  {
    icon: "📋",
    name: "Form / Download Gate",
    trigger: "webhook",
    description: "Your form POSTs to a webhook URL. Deliver a lead magnet, nurture with 3 follow-ups.",
    cooldownHours: 168, // 7 days
  },
  {
    icon: "💬",
    name: "Post-Chat Follow-Up",
    trigger: "conversation_ended",
    description: "After every resolved chat, send a CSAT request and a helpful follow-up resource.",
    cooldownHours: 24,
  },
  {
    icon: "😟",
    name: "Unhappy Customer Recovery",
    trigger: "conversation_negative_sentiment",
    description: "When AI detects frustration, automatically send an apology + escalation offer.",
    cooldownHours: 48,
  },
  {
    icon: "👋",
    name: "New Contact Welcome",
    trigger: "contact_created",
    description: "Welcome every new contact with an introduction, value prop, and next steps.",
    cooldownHours: 0,
  },
  {
    icon: "🔄",
    name: "Re-Engagement",
    trigger: "manual_enroll",
    description: "Manually enroll dormant contacts in a win-back sequence.",
    cooldownHours: 720, // 30 days
  },
];
const BLANK_TEMPLATE = { name: "", category: "custom", subject: "", bodyHtml: "", bodyText: "", fromName: "", fromAddress: "" };

function SequencesTab() {
  const api = useApi();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [editCampaign, setEditCampaign] = useState<Campaign | null | "new">(null);
  const [editTemplate, setEditTemplate] = useState<Template | null | "new">(null);
  const [subtab, setSubtab] = useState<"campaigns" | "templates">("campaigns");
  const [showStarters, setShowStarters] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, tRes] = await Promise.all([
        api.admin.get("EmailCampaigns"),
        api.admin.get("EmailCampaigns/Templates"),
      ]);
      setCampaigns(Array.isArray(await cRes.json()) ? await (await api.admin.get("EmailCampaigns")).json() : []);
      setTemplates(Array.isArray(await tRes.json()) ? await (await api.admin.get("EmailCampaigns/Templates")).json() : []);
    } catch { setCampaigns([]); setTemplates([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCampaign = async (data: typeof BLANK_CAMPAIGN) => {
    const id = editCampaign !== "new" ? (editCampaign as Campaign)?.id : undefined;
    if (id) await api.admin.put(`EmailCampaigns/${id}`, data);
    else     await api.admin.post("EmailCampaigns", data);
    setEditCampaign(null); await load();
  };
  const toggleCampaign = async (id: number) => { await api.admin.patch(`EmailCampaigns/${id}/toggle`, {}); await load(); };
  const deleteCampaign = async (id: number) => { if (!confirm("Delete this sequence?")) return; await api.admin.delete(`EmailCampaigns/${id}`); await load(); };
  const saveTemplate = async (data: typeof BLANK_TEMPLATE) => {
    const id = editTemplate !== "new" ? (editTemplate as Template)?.id : undefined;
    if (id) await api.admin.put(`EmailCampaigns/Templates/${id}`, data);
    else     await api.admin.post("EmailCampaigns/Templates", data);
    setEditTemplate(null); await load();
  };
  const deleteTemplate = async (id: number) => { if (!confirm("Delete this template?")) return; await api.admin.delete(`EmailCampaigns/Templates/${id}`); await load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex border-b border-gray-200">
          {(["campaigns", "templates"] as const).map(t => (
            <button key={t} onClick={() => setSubtab(t)}
              className={`px-4 py-2 text-sm border-b-2 -mb-px capitalize ${subtab === t ? "border-blue-500 text-blue-600 font-medium" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
              {t === "campaigns" ? "Sequences" : "Templates"}
              <span className="ml-1.5 text-xs bg-gray-100 text-gray-500 rounded-full px-1.5 py-0.5">
                {t === "campaigns" ? campaigns.length : templates.length}
              </span>
            </button>
          ))}
        </div>
        <button onClick={() => subtab === "campaigns" ? setEditCampaign("new") : setEditTemplate("new")}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
          {subtab === "campaigns" ? "+ New Sequence" : "+ New Template"}
        </button>
        {subtab === "templates" && (
          <button onClick={() => setShowStarters(true)}
            className="ml-2 px-4 py-2 border border-blue-200 text-blue-700 text-sm rounded-lg hover:bg-blue-50">
            ð Starter Library
          </button>
        )}
      </div>

      {loading ? <div className="py-8 text-center text-gray-400 text-sm">Loading…</div> : subtab === "campaigns" ? (
        <div className="space-y-2">
          {campaigns.length === 0 && (
            <div className="space-y-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Quick Start — pick a flow to set up</p>
              <div className="grid grid-cols-2 gap-3">
                {SEQUENCE_QUICK_STARTS.map(qs => (
                  <button key={qs.trigger} onClick={() => setEditCampaign({ ...BLANK_CAMPAIGN, name: qs.name, triggerType: qs.trigger, enrollmentCooldownHours: qs.cooldownHours } as any)}
                    className="text-left p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-colors">
                    <div className="text-2xl mb-1">{qs.icon}</div>
                    <p className="text-sm font-medium text-gray-900">{qs.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{qs.description}</p>
                  </button>
                ))}
              </div>
              <p className="text-center text-xs text-gray-400">Or <button onClick={() => setEditCampaign("new")} className="text-blue-600 hover:underline">start from scratch</button></p>
            </div>
          )}
          {campaigns.map(c => {
            const trigger = TRIGGER_TYPES.find(t => t.value === c.triggerType);
            return (
              <div key={c.id} className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-4">
                  <button onClick={() => toggleCampaign(c.id)}
                    className={`w-10 h-5 rounded-full relative transition-colors ${c.isActive ? "bg-green-500" : "bg-gray-300"}`}>
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${c.isActive ? "translate-x-5" : "translate-x-0.5"}`} />
                  </button>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-gray-500">{trigger?.label ?? c.triggerType} · {c.steps.length} step{c.steps.length !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 ml-4">
                  <div className="text-right text-xs">
                    <p className="font-semibold text-gray-900">{c.stats.enrolled}</p>
                    <p className="text-gray-400">enrolled</p>
                  </div>
                  <div className="text-right text-xs">
                    <p className="font-semibold text-gray-900">{c.stats.sent}</p>
                    <p className="text-gray-400">sent</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditCampaign(c)} className="text-xs px-3 py-1 border border-gray-200 rounded hover:bg-gray-50">Edit</button>
                    <button onClick={() => deleteCampaign(c.id)} className="text-xs px-3 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50">Delete</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {templates.length === 0 && <p className="text-center py-8 text-gray-400 text-sm">No templates yet.</p>}
          {templates.map(t => (
            <div key={t.id} className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-gray-200">
              <div>
                <p className="text-sm font-medium text-gray-900">{t.name}</p>
                <p className="text-xs text-gray-500">{t.category} · {t.subject}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditTemplate(t)} className="text-xs px-3 py-1 border border-gray-200 rounded hover:bg-gray-50">Edit</button>
                <button onClick={() => deleteTemplate(t.id)} className="text-xs px-3 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editCampaign && <CampaignEditorModal campaign={editCampaign === "new" ? null : editCampaign as Campaign} templates={templates} onSave={saveCampaign} onClose={() => setEditCampaign(null)} />}
      {editTemplate && <TemplateEditorModal template={editTemplate === "new" ? null : editTemplate as Template} onSave={saveTemplate} onClose={() => setEditTemplate(null)} />}
      {showStarters && <StarterTemplatesModal onClose={() => setShowStarters(false)} onImported={() => { setShowStarters(false); load(); }} />}
    </div>
  );
}

function CampaignEditorModal({ campaign, templates, onSave, onClose }: { campaign: Campaign | null; templates: Template[]; onSave: (d: typeof BLANK_CAMPAIGN) => Promise<void>; onClose: () => void }) {
  const [form, setForm] = useState<typeof BLANK_CAMPAIGN>(campaign ? { ...BLANK_CAMPAIGN, ...campaign, requiredSubscriptionFlag: campaign.requiredSubscriptionFlag ?? "" } : { ...BLANK_CAMPAIGN });
  const [saving, setSaving] = useState(false);
  const triggerDef = TRIGGER_TYPES.find(t => t.value === form.triggerType);

  const addStep = () => setForm(f => ({ ...f, steps: [...f.steps, { stepOrder: f.steps.length, delayDays: f.steps.length === 0 ? 0 : 1, delayHours: 0, templateId: templates[0]?.id ?? 0 }] }));
  const updateStep = (i: number, field: string, value: any) => setForm(f => { const steps = [...f.steps]; steps[i] = { ...steps[i], [field]: value }; return { ...f, steps }; });
  const removeStep = (i: number) => setForm(f => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold">{campaign ? "Edit Sequence" : "New Sequence"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          <div><label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
            <input className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Post-Chat Follow-Up" /></div>
          <div><label className="block text-xs font-medium text-gray-500 mb-1">Trigger</label>
            <select className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm" value={form.triggerType} onChange={e => setForm(f => ({ ...f, triggerType: e.target.value }))}>
              {TRIGGER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              <optgroup label="Coming Soon">{COMING_SOON_TRIGGERS.map(t => <option key={t.value} value={t.value} disabled>{t.label} (coming soon)</option>)}</optgroup>
            </select>
            {triggerDef && <p className="text-xs text-gray-400 mt-1">{triggerDef.description}</p>}
            {/* Webhook URL — shown only when webhook trigger selected and campaign exists */}
            {form.triggerType === "webhook" && campaign?.webhookToken && (
              <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1">
                <p className="text-xs font-medium text-slate-600">Your Webhook URL</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-white border border-slate-200 rounded px-2 py-1 break-all text-slate-700">
                    {`${window.location.origin}/api/public/CampaignWebhook/${campaign.webhookToken}`}
                  </code>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(`${window.location.origin}/api/public/CampaignWebhook/${campaign.webhookToken}`)}
                    className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-100 whitespace-nowrap">
                    Copy
                  </button>
                </div>
                <p className="text-xs text-slate-400">POST <code className="text-slate-600">{"{ email, name, ...any fields }"}</code> to this URL from your form, Zapier, or Make.com. Works with plain HTML forms too.</p>
              </div>
            )}
            {form.triggerType === "webhook" && !campaign?.webhookToken && (
              <p className="text-xs text-amber-600 mt-1 bg-amber-50 rounded px-2 py-1">Save the sequence first to get your webhook URL.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-500 mb-1">Re-enroll cooldown (hours)</label>
              <input type="number" min="0" className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm" value={form.enrollmentCooldownHours} onChange={e => setForm(f => ({ ...f, enrollmentCooldownHours: +e.target.value }))} /></div>
            <div><label className="block text-xs font-medium text-gray-500 mb-1">Max enrollments per contact</label>
              <input type="number" min="0" className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm" value={form.maxEnrollmentsPerContact} onChange={e => setForm(f => ({ ...f, maxEnrollmentsPerContact: +e.target.value }))} /></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-500">Steps ({form.steps.length})</label>
              <button onClick={addStep} disabled={templates.length === 0} className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40">+ Add Step</button>
            </div>
            {templates.length === 0 && <p className="text-xs text-amber-600 bg-amber-50 rounded px-3 py-2">Create a template first, then add steps.</p>}
            <div className="space-y-2">
              {form.steps.map((step, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 rounded px-3 py-2 border border-gray-200">
                  <span className="text-xs font-medium text-gray-400 w-6">#{i + 1}</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500">Delay:</span>
                    <input type="number" min="0" className="w-14 border border-gray-200 rounded px-2 py-0.5 text-xs text-center" value={step.delayDays} onChange={e => updateStep(i, "delayDays", +e.target.value)} />
                    <span className="text-xs text-gray-500">days</span>
                    <input type="number" min="0" max="23" className="w-14 border border-gray-200 rounded px-2 py-0.5 text-xs text-center" value={step.delayHours} onChange={e => updateStep(i, "delayHours", +e.target.value)} />
                    <span className="text-xs text-gray-500">hours</span>
                  </div>
                  <select className="flex-1 border border-gray-200 rounded px-2 py-0.5 text-xs" value={step.templateId} onChange={e => updateStep(i, "templateId", +e.target.value)}>
                    <option value={0}>— select template —</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <button onClick={() => removeStep(i)} className="text-red-400 hover:text-red-600 text-sm px-1">✕</button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-1.5 text-sm border border-gray-200 rounded">Cancel</button>
          <button onClick={async () => { setSaving(true); try { await onSave(form); } finally { setSaving(false); } }}
            disabled={saving || !form.name.trim() || form.steps.length === 0}
            className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-40">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplateEditorModal({ template, onSave, onClose }: { template: Template | null; onSave: (d: typeof BLANK_TEMPLATE) => Promise<void>; onClose: () => void }) {
  const [form, setForm] = useState<typeof BLANK_TEMPLATE>(template ?? { ...BLANK_TEMPLATE });
  const [saving, setSaving] = useState(false);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold">{template ? "Edit Template" : "New Template"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-500 mb-1">Template Name</label>
              <input className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Welcome Email Day 0" /></div>
            <div><label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
              <select className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {["onboarding","followup","winback","billing","custom"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <TemplateEditor template={form} onChange={(field, val) => setForm(f => ({ ...f, [field]: val }))} />
        </div>
        <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-1.5 text-sm border border-gray-200 rounded">Cancel</button>
          <button onClick={async () => { setSaving(true); try { await onSave(form); } finally { setSaving(false); } }}
            disabled={saving || !form.name.trim() || !form.subject.trim()}
            className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-40">
            {saving ? "Saving…" : "Save Template"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Settings Tab ─────────────────────────────────────────────────────────────

function SettingsTab({ quota }: { quota: Quota }) {
  const now = new Date();
  const daysLeft = new Date(now.getFullYear(), now.getMonth() + 1, 1).getDate() - now.getDate();
  const pctUsed = quota.maxEmailsPerMonth > 0 ? quota.currentMonthEmailCount / quota.maxEmailsPerMonth * 100 : 0;
  const nearLimit = pctUsed >= 80;

  return (
    <div className="max-w-xl space-y-6">
      {/* Plan summary */}
      {quota.maxEmailsPerMonth > 0 && (
        <div className={`rounded-xl p-4 flex items-start gap-4 ${nearLimit ? "bg-amber-50 border border-amber-200" : "bg-blue-50 border border-blue-100"}`}>
          <div className="flex-1">
            <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${nearLimit ? "text-amber-600" : "text-blue-600"}`}>Your Plan</p>
            <p className={`text-xl font-bold ${nearLimit ? "text-amber-700" : "text-blue-700"}`}>
              {quota.maxEmailsPerMonth.toLocaleString()} emails / month
            </p>
            <p className={`text-sm mt-0.5 ${nearLimit ? "text-amber-600" : "text-blue-500"}`}>
              {quota.remainingEmails?.toLocaleString() ?? "—"} remaining · resets in {daysLeft} day{daysLeft !== 1 ? "s" : ""}
            </p>
          </div>
          {nearLimit && (
            <div className="text-xs text-amber-700 font-medium bg-amber-100 border border-amber-200 rounded px-2 py-1 flex-shrink-0">
              {pctUsed >= 100 ? "Limit reached" : `${Math.round(pctUsed)}% used`}
            </div>
          )}
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Monthly Usage</h3>
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">Emails sent this month</span>
              <span className="text-sm font-semibold text-gray-900">
                {quota.currentMonthEmailCount.toLocaleString()}
                {quota.maxEmailsPerMonth > 0 && ` / ${quota.maxEmailsPerMonth.toLocaleString()}`}
              </span>
            </div>
            {quota.maxEmailsPerMonth > 0 && (
              <div className="h-2 bg-gray-100 rounded-full">
                <div className="h-2 bg-blue-500 rounded-full transition-all"
                  style={{ width: `${Math.min(100, quota.currentMonthEmailCount / quota.maxEmailsPerMonth * 100)}%` }} />
              </div>
            )}
            <p className="text-xs text-gray-400 mt-1">Resets in ~{daysLeft} day{daysLeft !== 1 ? "s" : ""}</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">Total contacts</span>
              <span className="text-sm font-semibold text-gray-900">
                {quota.contactCount.toLocaleString()}
                {quota.maxContacts > 0 && ` / ${quota.maxContacts.toLocaleString()}`}
              </span>
            </div>
            {quota.maxContacts > 0 && (
              <div className="h-2 bg-gray-100 rounded-full">
                <div className="h-2 bg-green-500 rounded-full transition-all"
                  style={{ width: `${Math.min(100, quota.contactCount / quota.maxContacts * 100)}%` }} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Channel Status</h3>
        <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-gray-700">📧 Email</span>
            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">Active</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-gray-700">📱 SMS</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${quota.enableSms ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {quota.enableSms ? "Active" : "Not enabled"}
            </span>
          </div>
        </div>
        {!quota.enableSms && <p className="text-xs text-gray-400 mt-2">SMS campaigns require a dedicated phone number. Contact support to enable.</p>}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Need more?</h3>
        <p className="text-sm text-gray-500">To increase your contact or email limits, contact <a href="mailto:support@velaro.com" className="text-blue-600 underline">support@velaro.com</a>.</p>
      </div>

      <div>
        <SenderDomainsPanel />
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Suppression List</h3>
        <SuppressionImport />
      </div>
    </div>
  );
}


// ─── Starter Template Library Modal ─────────────────────────────────────────

interface StarterMeta { key: string; name: string; category: string; description: string; tags: string[]; subject: string; }

function StarterTemplatesModal({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const api = useApi();
  const [starters, setStarters] = React.useState<StarterMeta[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [importing, setImporting] = React.useState<string | null>(null);
  const [preview, setPreview] = React.useState<StarterMeta | null>(null);
  const [filter, setFilter] = React.useState('');

  React.useEffect(() => {
    api.admin.get('EmailCampaigns/StarterTemplates')
      .then(r => r.json())
      .then(d => setStarters(Array.isArray(d) ? d : []))
      .catch(() => setStarters([]))
      .finally(() => setLoading(false));
  }, []);

  const handleImport = async (key: string) => {
    setImporting(key);
    try {
      const r = await api.admin.post(`EmailCampaigns/Templates/ImportStarter/${key}`, {});
      if (!r.ok) throw new Error('Import failed');
      onImported();
    } catch {
      alert('Failed to import template. Please try again.');
      setImporting(null);
    }
  };

  const categories = ['all', ...Array.from(new Set(starters.map(s => s.category)))];
  const [activeCategory, setActiveCategory] = React.useState('all');

  const filtered = starters.filter(s =>
    (activeCategory === 'all' || s.category === activeCategory) &&
    (!filter || s.name.toLowerCase().includes(filter.toLowerCase()) || s.description.toLowerCase().includes(filter.toLowerCase()))
  );

  const CATEGORY_LABELS: Record<string, string> = {
    all: 'All', onboarding: 'Onboarding', winback: 'Win-Back',
    marketing: 'Marketing', transactional: 'Transactional'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">📚 Starter Template Library</h2>
            <p className="text-sm text-gray-500 mt-0.5">Professional templates ready to customize. Importing creates a copy in your library.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-100 bg-gray-50">
          <input
            type="text" placeholder="Search templates…" value={filter} onChange={e => setFilter(e.target.value)}
            className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-400"
          />
          <div className="flex gap-1">
            {categories.map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1 text-xs rounded-full ${activeCategory === cat ? "bg-blue-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                {CATEGORY_LABELS[cat] ?? cat}
              </button>
            ))}
          </div>
        </div>

        {/* Template grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-400 text-sm">Loading templates…</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(s => (
                <div key={s.key} className="border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm transition-all">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{CATEGORY_LABELS[s.category] ?? s.category}</span>
                    <div className="flex gap-1">
                      {(s.tags ?? []).slice(0, 2).map(tag => (
                        <span key={tag} className="text-xs bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <p className="font-semibold text-gray-900 text-sm mb-1">{s.name}</p>
                  <p className="text-xs text-gray-500 mb-3 line-clamp-2">{s.description}</p>
                  <p className="text-xs text-gray-400 italic mb-3 truncate">✉️ {s.subject}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleImport(s.key)}
                      disabled={importing === s.key}
                      className="flex-1 text-xs py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                      {importing === s.key ? 'Importing…' : '+ Use This Template'}
                    </button>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && <p className="col-span-3 text-center py-8 text-gray-400 text-sm">No templates match your search.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Suppression List Import ──────────────────────────────────────────────────

function SuppressionImport() {
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setStatus("Uploading…");
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await fetch("/api/admin/ContactLists/ImportSuppression", { method: "POST", body: form, credentials: "include" });
      const d = await r.json();
      if (!r.ok) { setStatus(`Error: ${d.error}`); return; }
      setStatus(d.message ?? "Done.");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch { setStatus("Upload failed."); }
    finally { setUploading(false); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
      <div>
        <p className="text-sm font-medium text-gray-900">Import Suppression List</p>
        <p className="text-xs text-gray-500 mt-0.5">Upload a CSV of email addresses that should never receive campaigns (bounced, opted-out, legal holds).</p>
      </div>
      <p className="text-xs text-gray-400">Format: single column CSV with header <code>email</code>. Max 5 MB.</p>
      <div className="flex gap-2 items-center">
        <input ref={fileRef} type="file" accept=".csv" onChange={e => setFile(e.target.files?.[0] ?? null)}
          className="text-xs text-gray-600 file:mr-2 file:text-xs file:px-2 file:py-1 file:border file:border-gray-200 file:rounded file:bg-white" />
        <button onClick={handleUpload} disabled={!file || uploading}
          className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded disabled:opacity-40 whitespace-nowrap">
          {uploading ? "Uploading…" : "Import"}
        </button>
      </div>
      {status && <p className="text-xs text-gray-600">{status}</p>}
    </div>
  );
}

// ─── Sender Responsibility Terms Modal ────────────────────────────────────────

function TermsModal({ onAccept, onClose }: { onAccept: () => void; onClose: () => void }) {
  const api = useApi();
  const [accepting, setAccepting] = useState(false);

  const accept = async () => {
    setAccepting(true);
    try {
      await api.admin.post("Campaigns/AcceptTerms", {});
      onAccept();
    } catch { setAccepting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="bg-blue-600 px-6 py-4 text-white flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold">Before You Send — Review Sending Policy</h2>
            <p className="text-xs text-blue-200 mt-0.5">Required once per account</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-blue-200 hover:text-white ml-4 mt-0.5 text-lg leading-none">✕</button>
        </div>
        <div className="p-6 space-y-4 text-sm text-gray-700">
          <p>By using Outbound Campaigns, you agree to the following:</p>
          <ul className="space-y-2">
            {[
              "You own or have permission to email every contact on your list.",
              "You will not send to purchased, rented, or scraped lists.",
              "Every email includes a clear, working unsubscribe link (automatically added).",
              "You comply with CAN-SPAM, GDPR, CASL, and other applicable laws in your recipients' countries.",
              "Your sending domain (From address) is one you control and can configure SPF/DKIM/DMARC for.",
              "You are responsible for your sender reputation — Velaro will auto-pause accounts with >5% bounce or >0.3% complaint rates.",
              "You will not send deceptive, misleading, or harassing content.",
            ].map((item, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-blue-500 font-bold shrink-0">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-400">
            Velaro uses Azure Communication Services for sending. Bounce and complaint data is automatically
            processed to protect deliverability for all customers. Accounts that repeatedly violate sending
            policies will be suspended.
          </p>
        </div>
        <div className="px-6 pb-6 flex justify-between items-center gap-3">
          <a href="mailto:support@velaro.com" className="text-xs text-gray-400 hover:text-gray-600 underline">
            Questions? Contact support
          </a>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="px-4 py-1.5 text-sm border border-gray-200 rounded text-gray-600 hover:bg-gray-50">
              Not now
            </button>
            <button onClick={accept} disabled={accepting}
              className="px-5 py-1.5 text-sm bg-blue-600 text-white rounded font-medium hover:bg-blue-700 disabled:opacity-50">
              {accepting ? "Saving…" : "I Accept — Activate Campaigns"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Pre-send Checklist ───────────────────────────────────────────────────────

function PreSendChecklist({ subject, bodyHtml, bodyText, fromAddress, fromName, listId, quota }: {
  subject: string; bodyHtml: string; bodyText: string; fromAddress: string; fromName: string;
  listId: number | null; quota: Quota;
}) {
  const SPAM_WORDS = ["FREE!!!", "WINNER", "CONGRATULATIONS", "URGENT", "ACT NOW", "LIMITED TIME", "CLICK HERE", "BUY NOW", "EARN MONEY"];
  const subjectUpper = subject?.toUpperCase() ?? "";
  const spamHits = SPAM_WORDS.filter(w => subjectUpper.includes(w));

  const checks = [
    { ok: !!subject?.trim(), label: "Subject line", detail: "Required for deliverability" },
    { ok: !!fromName?.trim(), label: "From name set", detail: "Builds recipient trust" },
    { ok: !!fromAddress?.trim() && fromAddress.includes("@"), label: "From address valid", detail: "Required by CAN-SPAM" },
    { ok: !!bodyHtml?.trim() || !!bodyText?.trim(), label: "Email body", detail: "Cannot send empty email" },
    { ok: !!bodyText?.trim(), label: "Plain text version", detail: "Improves inbox placement — some clients show text only", warn: true },
    { ok: !!listId, label: "Audience selected", detail: "Pick a contact list or segment" },
    { ok: spamHits.length === 0, label: "Subject line spam check", detail: spamHits.length ? `Detected: ${spamHits.join(", ")}` : "No spam triggers found", warn: spamHits.length > 0 },
    { ok: !quota.maxEmailsPerMonth || (quota.remainingEmails ?? Infinity) > 0, label: "Monthly quota available", detail: quota.remainingEmails != null ? `${quota.remainingEmails.toLocaleString()} sends remaining` : "No limit" },
  ];

  const errors = checks.filter(c => !c.ok && !c.warn);
  const warnings = checks.filter(c => !c.ok && c.warn);

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Pre-send Checklist</h4>
      {checks.map((c, i) => (
        <div key={i} className={`flex items-start gap-2 text-sm p-2 rounded ${c.ok ? "bg-green-50" : c.warn ? "bg-yellow-50" : "bg-red-50"}`}>
          <span className={`font-bold shrink-0 ${c.ok ? "text-green-600" : c.warn ? "text-yellow-600" : "text-red-600"}`}>
            {c.ok ? "✓" : c.warn ? "⚠" : "✗"}
          </span>
          <div>
            <p className={`font-medium ${c.ok ? "text-green-800" : c.warn ? "text-yellow-800" : "text-red-800"}`}>{c.label}</p>
            <p className={`text-xs ${c.ok ? "text-green-600" : c.warn ? "text-yellow-700" : "text-red-600"}`}>{c.detail}</p>
          </div>
        </div>
      ))}
      {errors.length > 0 && (
        <p className="text-xs text-red-600 font-medium mt-1">Fix {errors.length} issue{errors.length !== 1 ? "s" : ""} before sending.</p>
      )}
      {errors.length === 0 && warnings.length === 0 && (
        <p className="text-xs text-green-600 font-medium mt-1">All checks passed — ready to send!</p>
      )}
    </div>
  );
}

// ─── Reports Tab ──────────────────────────────────────────────────────────────

interface ReportSummary { totalSent: number; totalOpened: number; totalClicked: number; avgOpenRate: number; avgClickRate: number; campaignCount: number; }
interface CampaignRow { id: number; name: string; channel: string; sentAt: string; sentCount: number; openedCount: number; clickedCount: number; openRate: number; clickRate: number; }
interface MonthlyRow { month: string; sent: number; opened: number; clicked: number; }
interface DeliverabilityData { totalSent: number; bounceRate: number; bounceStatus: string; totalFailed: number; unsubRate: number; unsubStatus: string; unsubCount: number; openRate: number; openStatus: string; recommendations: string[]; }

function ReportsTab() {
  const api = useApi();
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [monthly, setMonthly] = useState<MonthlyRow[]>([]);
  const [deliv, setDeliv] = useState<DeliverabilityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<"sentAt" | "openRate" | "clickRate" | "sentCount">("sentAt");
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [pr, mr, dr] = await Promise.all([
          api.admin.get("CampaignReports/Performance").then(r => r.json()),
          api.admin.get("CampaignReports/Monthly").then(r => r.json()),
          api.admin.get("CampaignReports/Deliverability").then(r => r.json()),
        ]);
        setSummary(pr?.summary ?? null);
        setCampaigns(Array.isArray(pr?.campaigns) ? pr.campaigns : []);
        setMonthly(Array.isArray(mr) ? mr : []);
        setDeliv(dr ?? null);
      } catch {}
      finally { setLoading(false); }
    })();
  }, [api.messaging]);

  const sorted = [...campaigns].sort((a, b) => {
    const av = a[sortField] as any, bv = b[sortField] as any;
    return sortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });
  const toggleSort = (f: typeof sortField) => { if (sortField === f) setSortAsc(x => !x); else { setSortField(f); setSortAsc(false); } };
  const statusBg = (s: string) => s === "green" ? "bg-green-100 text-green-700" : s === "yellow" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700";

  if (loading) return <div className="py-16 text-center text-gray-400 text-sm">Loading reports…</div>;

  return (
    <div className="space-y-8">
      {/* KPI Cards */}
      {summary && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Last 90 Days</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: "Campaigns", value: summary.campaignCount.toLocaleString(), color: "text-slate-700" },
              { label: "Emails Sent", value: summary.totalSent.toLocaleString(), color: "text-slate-700" },
              { label: "Avg Open Rate", value: `${summary.avgOpenRate}%`, color: "text-emerald-600" },
              { label: "Avg Click Rate", value: `${summary.avgClickRate}%`, color: "text-blue-600" },
              { label: "Total Clicks", value: summary.totalClicked.toLocaleString(), color: "text-blue-600" },
            ].map((k, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
                <p className="text-xs text-gray-500 mt-1">{k.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Volume — recharts AreaChart */}
      {monthly.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Monthly Volume (Last 12 Months)</h3>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={monthly} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#94a3b8" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="colorOpened" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="colorClicked" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="month"
                  tickFormatter={(v: any) => typeof v === "string" ? v.slice(5) : v}
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={40} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                  formatter={(value: any, name: any) => [typeof value === "number" ? value.toLocaleString() : value, typeof name === "string" ? name.charAt(0).toUpperCase() + name.slice(1) : name]}
                  labelFormatter={(label: any) => `Month: ${label}`}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="sent" stroke="#94a3b8" strokeWidth={2} fill="url(#colorSent)" name="sent" />
                <Area type="monotone" dataKey="opened" stroke="#3b82f6" strokeWidth={2} fill="url(#colorOpened)" name="opened" />
                <Area type="monotone" dataKey="clicked" stroke="#10b981" strokeWidth={2} fill="url(#colorClicked)" name="clicked" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Open & Click Rate by Campaign — recharts horizontal BarChart (top 10 by open rate) */}
      {campaigns.length > 0 && (() => {
        const top10 = [...campaigns]
          .filter(c => c.sentCount > 0)
          .sort((a, b) => b.openRate - a.openRate)
          .slice(0, 10)
          .map(c => ({
            name: c.name.length > 22 ? c.name.slice(0, 20) + "…" : c.name,
            "Open %": c.openRate,
            "Click %": c.clickRate,
          }));
        if (top10.length === 0) return null;
        return (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Open & Click Rate by Campaign (Top 10)</h3>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <ResponsiveContainer width="100%" height={Math.max(180, top10.length * 38)}>
                <BarChart data={top10} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v: any) => `${v}%`} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} width={130} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                    formatter={(value: any) => [`${value}%`]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Open %" fill="#10b981" radius={[0, 4, 4, 0]} maxBarSize={16} />
                  <Bar dataKey="Click %" fill="#3b82f6" radius={[0, 4, 4, 0]} maxBarSize={16} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })()}

      {/* Deliverability Health */}
      {deliv && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Deliverability Health</h3>
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Bounce Rate", value: `${deliv.bounceRate}%`, status: deliv.bounceStatus, sub: `${deliv.totalFailed.toLocaleString()} bounces` },
                { label: "Unsubscribe Rate", value: `${deliv.unsubRate}%`, status: deliv.unsubStatus, sub: `${deliv.unsubCount.toLocaleString()} unsubs` },
                { label: "Open Rate (Health)", value: `${deliv.openRate}%`, status: deliv.openStatus, sub: "Low = possible spam folder" },
              ].map((m, i) => (
                <div key={i} className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold text-gray-900">{m.value}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{m.label}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-1 inline-block ${statusBg(m.status)}`}>
                    {m.status === "green" ? "Healthy" : m.status === "yellow" ? "Warning" : "Critical"}
                  </span>
                  <p className="text-[10px] text-gray-400 mt-1">{m.sub}</p>
                </div>
              ))}
            </div>
            {deliv.recommendations.length > 0 && (
              <div className="space-y-1.5">
                {deliv.recommendations.map((r, i) => (
                  <div key={i} className="flex gap-2 text-sm">
                    <span className="text-blue-500 shrink-0">→</span>
                    <span className="text-gray-700">{r}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400">Industry thresholds: bounce &lt;2% = healthy, &gt;5% = danger. Unsubscribe &lt;0.5% = healthy. Open rate &gt;20% = healthy.</p>
          </div>
        </div>
      )}

      {/* Campaign Performance Table */}
      {sorted.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Campaign Performance</h3>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Campaign</th>
                  {(["sentCount", "openRate", "clickRate", "sentAt"] as const).map(f => (
                    <th key={f} className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700" onClick={() => toggleSort(f)}>
                      { f === "sentCount" ? "Sent" : f === "openRate" ? "Open %" : f === "clickRate" ? "Click %" : "Date" }
                      {sortField === f && <span className="ml-1">{sortAsc ? "↑" : "↓"}</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-gray-900 truncate max-w-[200px]">{c.name}</p>
                      <p className="text-xs text-gray-400">{c.channel}</p>
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-700">{c.sentCount.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`font-medium ${c.openRate >= 20 ? "text-emerald-600" : c.openRate >= 10 ? "text-yellow-600" : "text-red-600"}`}>{c.openRate}%</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="font-medium text-blue-600">{c.clickRate}%</span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs text-gray-400">{c.sentAt ? new Date(c.sentAt).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {sorted.length === 0 && !loading && (
        <div className="text-center py-16 text-gray-400">
          <p className="text-sm">No sent campaigns yet. Reports will appear here after your first send.</p>
        </div>
      )}

      {/* Platform comparison */}
      <ComparisonChart />
    </div>
  );
}

// ─── Sender Domains Panel ─────────────────────────────────────────────────────

interface SenderDomain { id: number; domainName: string; status: string; isDefault: boolean; createdAt: string; dnsRecords?: DnsRec[]; }
interface DnsRec { type: string; host: string; value: string; required: boolean; verified: boolean; description: string; helpUrl: string; }

function SenderDomainsPanel() {
  const api = useApi();
  const [domains, setDomains] = useState<SenderDomain[]>([]);
  const [adding, setAdding] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [selected, setSelected] = useState<SenderDomain | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.admin.get("SenderDomains");
      const d = await r.json();
      setDomains(Array.isArray(d) ? d : []);
    } catch {}
    finally { setLoading(false); }
  }, [api.admin]);

  useEffect(() => { load(); }, [load]);

  // Fetch full domain detail (including DNS records) when user clicks "View DNS"
  const viewDns = async (d: SenderDomain) => {
    if (selected?.id === d.id) { setSelected(null); return; }
    try {
      const r = await api.admin.get(`SenderDomains/${d.id}`);
      if (r.ok) { setSelected(await r.json()); return; }
    } catch {}
    setSelected(d); // fallback to list item if detail fetch fails
  };

  const addDomain = async () => {
    if (!newDomain.trim()) return;
    setAdding(true);
    try {
      const r = await api.admin.post("SenderDomains", { domainName: newDomain.trim() });
      if (r.ok) {
        const d = await r.json();
        setSelected(d); // POST returns full dnsRecords
        setNewDomain("");
        await load();
      }
    } catch {}
    setAdding(false);
  };

  const verify = async (id: number) => {
    setVerifying(true);
    try {
      const r = await api.admin.post(`SenderDomains/${id}/Verify`, {});
      if (r.ok) {
        const d = await r.json();
        // Update selected with fresh DNS records from verify response
        setSelected(prev => prev?.id === id ? { ...prev, status: d.status, dnsRecords: d.dnsRecords } : prev);
        await load();
      }
    } catch {}
    setVerifying(false);
  };

  const statusBadge = (s: string) => s === "Verified"
    ? "bg-green-100 text-green-700"
    : s === "Partial" ? "bg-yellow-100 text-yellow-700"
    : "bg-gray-100 text-gray-600";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Custom Sending Domains</h4>
          <p className="text-xs text-gray-500 mt-0.5">Send from your own domain (mail.company.com) with SPF/DKIM/DMARC — your reputation, not ours.</p>
        </div>
      </div>

      {/* Add domain */}
      <div className="flex gap-2">
        <input className="flex-1 border border-gray-200 rounded px-3 py-1.5 text-sm" placeholder="mail.yourcompany.com"
          value={newDomain} onChange={e => setNewDomain(e.target.value)}
          onKeyDown={e => e.key === "Enter" && addDomain()} />
        <button onClick={addDomain} disabled={adding || !newDomain.trim()}
          className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-40 whitespace-nowrap">
          {adding ? "Adding…" : "+ Add Domain"}
        </button>
      </div>

      {/* Domain list */}
      {loading ? <p className="text-sm text-gray-400">Loading…</p> : domains.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded-xl p-6 text-center text-gray-400">
          <p className="text-sm">No custom domains yet.</p>
          <p className="text-xs mt-1">Add your domain above to set up SPF, DKIM, and DMARC.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {domains.map(d => (
            <div key={d.id} className="bg-white border border-gray-200 rounded-lg px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{d.domainName}</p>
                    {d.isDefault && <span className="text-[10px] text-blue-600">Default sending domain</span>}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge(d.status)}`}>{d.status}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => viewDns(d)}
                    className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 border border-gray-200 rounded">
                    {selected?.id === d.id ? "Hide DNS" : "View DNS"}
                  </button>
                  <button onClick={() => verify(d.id)} disabled={verifying}
                    className="text-xs px-3 py-1 border border-blue-200 text-blue-600 rounded hover:bg-blue-50">
                    {verifying ? "Checking…" : "Verify"}
                  </button>
                </div>
              </div>

              {/* DNS records table */}
              {selected?.id === d.id && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-600 mb-2">Add these DNS records at your registrar (Cloudflare, Route53, GoDaddy, etc.):</p>
                  <div className="space-y-2">
                    {(d.dnsRecords ?? []).map((rec, i) => (
                      <div key={i} className={`text-xs p-2.5 rounded-lg ${rec.verified ? "bg-green-50 border border-green-200" : rec.required ? "bg-yellow-50 border border-yellow-200" : "bg-gray-50 border border-gray-200"}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="space-y-1 min-w-0">
                            <div className="flex gap-2 items-center">
                              <span className={`font-mono font-bold ${rec.required ? "text-gray-800" : "text-gray-400"}`}>{rec.type}</span>
                              <span className="font-mono text-gray-600 truncate max-w-[200px]">{rec.host}</span>
                              {rec.required ? (
                                <span className="text-[9px] bg-blue-100 text-blue-700 px-1 rounded font-medium">REQUIRED</span>
                              ) : (
                                <span className="text-[9px] bg-gray-100 text-gray-500 px-1 rounded">OPTIONAL</span>
                              )}
                              {rec.verified && <span className="text-[9px] bg-green-100 text-green-700 px-1 rounded font-medium">VERIFIED ✓</span>}
                            </div>
                            <p className="font-mono text-gray-700 bg-white rounded px-2 py-1 border border-gray-200 break-all">{rec.value}</p>
                            <p className="text-gray-500">{rec.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">DNS changes can take up to 48 hours to propagate. Click Verify after adding all records.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function EmailCampaigns() {
  const api = useApi();
  const [quota, setQuota] = useState<Quota | null>(null);
  const [lists, setLists] = useState<ContactList[]>([]);
  const [tab, setTab] = useState<"contacts" | "broadcasts" | "sequences" | "reports" | "settings">("contacts");
  const [loading, setLoading] = useState(true);
  const [showTerms, setShowTerms] = useState(false);

  const loadQuotaAndLists = useCallback(async () => {
    setLoading(true);
    try {
      const [qr, lr] = await Promise.all([
        api.admin.get("Campaigns/Quota"),
        api.admin.get("ContactLists"),
      ]);
      const [qd, ld] = await Promise.all([qr.json(), lr.json()]);
      setQuota(qd);
      setLists(Array.isArray(ld) ? ld : []);
    } catch { setQuota(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadQuotaAndLists(); }, [loadQuotaAndLists]);

  useEffect(() => {
    if (quota?.enableOutboundCampaigns && !quota.termsAccepted) setShowTerms(true);
  }, [quota]);

  if (loading) return <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>;

  // Feature not enabled — show landing page
  if (!quota?.enableOutboundCampaigns) return <FeatureLandingPage />;

  const TABS = [
    { key: "contacts",   label: "Contacts" },
    { key: "broadcasts", label: "Broadcasts" },
    { key: "sequences",  label: "Sequences" },
    { key: "reports",    label: "Reports" },
    { key: "settings",   label: "Settings" },
  ] as const;

  const setupIncomplete = quota && (!quota.termsAccepted || !quota.hasVerifiedDomain);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {showTerms && (
        <TermsModal onAccept={() => { setShowTerms(false); loadQuotaAndLists(); }} onClose={() => setShowTerms(false)} />
      )}

      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Outbound Campaigns</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage contacts, send targeted broadcasts, and automate sequences triggered by real conversations.
        </p>
      </div>

      {/* Setup required banner — shown until terms accepted AND a verified domain exists */}
      {setupIncomplete && (
        <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">Complete sending setup before your first campaign</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Campaigns cannot send until you: {!quota.termsAccepted && <span className="font-medium">accept sender responsibility terms</span>}
              {!quota.termsAccepted && !quota.hasVerifiedDomain && " and "}
              {!quota.hasVerifiedDomain && <span className="font-medium">add and verify a custom sending domain</span>}.
              This protects your deliverability — emails come from your domain, not a shared address.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {!quota.termsAccepted && (
                <button onClick={() => setShowTerms(true)}
                  className="text-xs px-3 py-1.5 bg-amber-600 text-white rounded font-medium hover:bg-amber-700">
                  Accept Terms
                </button>
              )}
              {!quota.hasVerifiedDomain && (
                <button onClick={() => setTab("settings")}
                  className="text-xs px-3 py-1.5 border border-amber-400 text-amber-700 rounded font-medium hover:bg-amber-100">
                  Add Sending Domain →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === t.key ? "border-blue-500 text-blue-600 font-medium" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            {t.label}
            {t.key === "settings" && setupIncomplete && (
              <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-amber-400 align-middle" title="Setup required" />
            )}
          </button>
        ))}
      </div>

      {tab === "contacts"   && quota && <ContactsTab quota={quota} />}
      {tab === "broadcasts" && quota && <BroadcastsTab lists={lists} quota={quota} />}
      {tab === "sequences"  && <SequencesTab />}
      {tab === "reports"    && <ReportsTab />}
      {tab === "settings"   && quota && <SettingsTab quota={quota} />}
    </div>
  );
}
